import { RuntimeEnvironmentGSEnum } from "../app/RuntimeGS";
import type { StateConstructor, StateSetupGS } from "./StateContractsGS";
import type { StateGS } from "./StateGS";
import { parseUrlArgsGS } from "./UrlParserGS";

interface StateRegistrationGS {
    readonly name: string;
    readonly stateConstructor?: StateConstructor;
}

/** Mutable state registry used by environment runtime adapters. */
export class StateConfigurationGS {
    private readonly _states = new Map<string, StateRegistrationGS>();
    private _sealed = false;
    private _usePathRouting = false;
    private _defaultStateName = "main";
    private _state404Name = "";

    public constructor(private readonly _environment: RuntimeEnvironmentGSEnum) {
    }

    public apply(configure: (states: StateSetupGS) => void): void {
        const setup = Object.create(null) as StateSetupGS;
        Object.defineProperties(setup, {
            UsePathRouting: {
                enumerable: true,
                get: () => this.UsePathRouting,
                set: (value: boolean) => { this.UsePathRouting = value; }
            },
            DefaultStateName: {
                enumerable: true,
                get: () => this.DefaultStateName,
                set: (value: string) => { this.DefaultStateName = value; }
            },
            State404Name: {
                enumerable: true,
                get: () => this.State404Name,
                set: (value: string) => { this.State404Name = value; }
            },
            RegState: {
                enumerable: true,
                value: (constructor: StateConstructor) => this.registerState(constructor)
            },
            ReplaceState: {
                enumerable: true,
                value: (constructor: StateConstructor) => this.replaceState(constructor)
            }
        });

        try {
            configure(Object.freeze(setup));
        } finally {
            this._sealed = true;
        }
        this.validate();
    }

    public get UsePathRouting(): boolean { return this._usePathRouting; }
    public set UsePathRouting(value: boolean) {
        this.ensureMutable();
        this._usePathRouting = value;
    }

    public get DefaultStateName(): string { return this._defaultStateName; }
    public set DefaultStateName(value: string) {
        this.ensureMutable();
        this._defaultStateName = (value ?? "").trim();
    }

    public get State404Name(): string { return this._state404Name; }
    public set State404Name(value: string) {
        this.ensureMutable();
        this._state404Name = (value ?? "").trim();
    }

    private registerState(constructor: StateConstructor): void {
        this.ensureMutable();
        const registration = this.createRegistration(constructor);
        const key = registration.name.toLowerCase();
        if (this._states.has(key))
            throw new Error(`State '${registration.name}' is already registered.`);
        this._states.set(key, registration);
    }

    private replaceState(constructor: StateConstructor): void {
        this.ensureMutable();
        const registration = this.createRegistration(constructor);
        const key = registration.name.toLowerCase();
        if (!this._states.has(key))
            throw new Error(`State '${registration.name}' is not registered and cannot be replaced.`);
        this._states.set(key, registration);
    }

    public getStateConstructor(stateName: string): StateConstructor | undefined {
        return this._states.get((stateName ?? "").toLowerCase())?.stateConstructor;
    }

    public isStatePlaceholder(stateName: string): boolean {
        const registration = this._states.get((stateName ?? "").toLowerCase());
        return registration !== undefined && registration.stateConstructor === undefined;
    }

    public isStateRegistered(stateName: string): boolean {
        return this._states.has((stateName ?? "").toLowerCase());
    }

    public parseUrl(url: string): Record<string, string> | undefined {
        try {
            return parseUrlArgsGS(url, "http://gs.local");
        } catch {
            return undefined;
        }
    }

    public getStateName(args: Record<string, string>): string {
        return args[this.urlStateArgName] || this._defaultStateName;
    }

    public get urlStateArgName(): string {
        return this._usePathRouting ? "path0" : "state";
    }

    public buildStateUrl(stateName: string, args: Record<string, string> = {}): string {
        const pathParts: string[] = [];
        const query = new URLSearchParams();
        const pathKeys = new Set<string>();

        if (this._usePathRouting) {
            for (let index = 0; ; index++) {
                const key = `path${index}`;
                const value = args[key];
                if (value === undefined) break;
                pathParts.push(encodeURIComponent(value));
                pathKeys.add(key);
            }
            if (pathParts.length === 0 && stateName.toLowerCase() !== this._defaultStateName.toLowerCase())
                pathParts.push(encodeURIComponent(stateName));
        }

        for (const key of Object.keys(args)) {
            if (pathKeys.has(key) || (!this._usePathRouting && key === this.urlStateArgName)) continue;
            const value = args[key];
            if (value !== undefined) query.append(key, value);
        }

        if (!this._usePathRouting) query.set(this.urlStateArgName, stateName);
        const queryString = query.toString();
        if (this._usePathRouting) return `/${pathParts.join("/")}${queryString ? `?${queryString}` : ""}`;
        return `?${queryString}`;
    }

    public buildUrlForState(state: StateGS, extraArgs?: Record<string, string>): string {
        return this.buildStateUrl(state.name, { ...state.urlArgs, ...(extraArgs ?? {}) });
    }

    private createRegistration(constructor: StateConstructor): StateRegistrationGS {
        const name = (constructor?.StateName ?? "").trim();
        if (!name) throw new Error("StateName must not be empty.");

        const isServerPlaceholder = this._environment === RuntimeEnvironmentGSEnum.Server
            && constructor.IsPrivate === true;
        return {
            name,
            stateConstructor: isServerPlaceholder ? undefined : constructor
        };
    }

    private validate(): void {
        if (!this._defaultStateName)
            throw new Error("DefaultStateName must not be empty.");
        if (!this._states.has(this._defaultStateName.toLowerCase()))
            throw new Error(`Default state '${this._defaultStateName}' is not registered.`);

        if (!this._state404Name)
            throw new Error("State404Name must not be empty.");
        const state404 = this._states.get(this._state404Name.toLowerCase());
        if (!state404)
            throw new Error(`State404 '${this._state404Name}' is not registered.`);
        if (!state404.stateConstructor)
            throw new Error(`State404 '${this._state404Name}' cannot be a private server placeholder.`);
    }

    private ensureMutable(): void {
        if (this._sealed)
            throw new Error("State configuration is sealed and cannot be changed after initialization.");
    }
}
